import {put, call, all} from 'redux-saga/effects';
import Category from "../../entities/category";
import * as Actions from "../actions/categoryActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CategoryAction = {
    categoryId: string;
    category: Category;
    categories: Category[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCategory(action: CategoryAction) {
	const session = tabSession.captureGeneration();
	try {
		const category: Category = yield call(api.findOne, 'category', action.categoryId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CATEGORY_FETCH_ONE_SUCCESS, categoryId: action.categoryId, category, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CATEGORY_FETCH_ONE_FAILURE, categoryId: action.categoryId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CategoryAction} action
 */
export function* saveCategory(action: CategoryAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CATEGORY_SAVE saga', action.category?.id);

		if (action.category === undefined || action.category === null) {
			console.error('saveCategory received a null');
			throw new Error('saveCategory received a null');
		}

		if (!(action.category instanceof Category)) {
			action.category = new Category(action.category);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.category.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.category);
			const addResult = yield call(idb.add, 'category', action.category);
			if (addResult.isErr()) {
				console.error(`⮂ saveCategory: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCategorySave(action.category, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.CATEGORY_SAVE_FAILURE, category: action.category, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.CATEGORY_SAVE, category: action.category, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCategorySave(action.category, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.CATEGORY_SAVE_FAILURE, category: action.category, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CategoryAction} action
 * @param {Category[]} action.categories
 */
export function* saveManyCategories(action: CategoryAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CATEGORY_SAVE_MANY saga', action.categories.map(category => category.id).join(','));

        if (!Array.isArray(action.categories)) {
            console.error('saveManyCategories expected an array, received', action.categories);
            throw new Error('saveManyCategories expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveCategory), under these queue keys
		const dispatchedAddedOns: number[] = action.categories.map(category => category?.addedOn);
		const revertAll = () => action.categories.map((category, i) =>
			put(Actions.revertCategorySave(category, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Category[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.categories.length; i++) {
				const category = action.categories[i];
				category.persisted = false;
				ensureOperationId(category);
				toSave.push(category);
			}

			const addResult = yield call(idb.add, 'category', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyCategories: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.CATEGORY_SAVE_MANY_FAILURE, categories: action.categories, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.CATEGORY_SAVE_MANY, categories: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.CATEGORY_SAVE_MANY_FAILURE, categories: action.categories, status: 500, err});

			console.error(`Error saving ${action.categories.length} categories to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CategoryAction} action
 */
export function* removeCategory(action: CategoryAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.category === undefined || action.category === null || !(action.category instanceof Category)) {
			console.error('removeCategory expected type Category, received', action.category);
			reportQueued(false);
			throw new Error('removeCategory expected type Category');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.category.status = 'D';
		action.category.addedOn = newHRTimestamp();
		action.category.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.category);
		const addResult = yield call(idb.add, 'category', action.category);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.CATEGORY_REMOVE_FAILURE, category: action.category, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CATEGORY_REMOVE, category: action.category});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CATEGORY_REMOVE_FAILURE, category: action.category, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Category.
	*
	* @param {CategoryAction} action
	*/
export function* restoreCategory(action: CategoryAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCategory expected getCurrentUserId() to return a value');
        return;
    }

	if (action.category === undefined || action.category === null) {
        console.error('restoreCategory expected a Category, received null');
        throw new Error('restoreCategory expected a Category, received null');
    }
    else if (!(action.category instanceof Category)) action.category = new Category(action.category); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.category.status = 'U';
		action.category.addedOn = newHRTimestamp();
		action.category.addedBy = userId;
		action.category.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.category);
		const addResult = yield call(idb.add, 'category', action.category);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CATEGORY_RESTORE_FAILURE, category: action.category, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CATEGORY_RESTORE, category: action.category});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CATEGORY_RESTORE_FAILURE, category: action.category, status: 500, err});

		console.error('Error restoring from database', err);
	}
}